import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlagEnabled } from "posthog-js/react";
import {
  type Cell,
  type VisibilityState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { DocumentSummary } from "~/api/types";
import { CenterSpinner } from "~/atoms/spinner";
import { DataTable } from "~/molecules/DataTable";
import NoResult from "~/molecules/NoResult";
import { usePersistentState } from "~/hooks/usePersistentState";
import {
  StickyHorizontalScrollbar,
  hiddenNativeScrollbarClass,
} from "~/molecules/StickyHorizontalScrollbar";
import { useUserContext } from "~/hooks/useUserContext";
import { featureFlags } from "~/config/featureFlags";
import { DocumentActiveFilterChips } from "~/organisms/filters/DocumentActiveFilterChips";
import { DocumentFiltersPanel } from "~/organisms/filters/DocumentFiltersPanel";
import { FilterableContentLayout } from "~/organisms/filters/FilterableContentLayout";
import { FilterToggleButton } from "~/molecules/FilterToggleButton";
import SearchBar from "~/molecules/SearchBar";
import { Toolbar } from "~/molecules/Toolbar";
import { FieldEditDialog } from "./FieldEditDialog";
import { DocumentsColumnsMenu } from "./DocumentsColumnsMenu";
import {
  createDocumentsColumns,
  getDocumentsListColumnSpecs,
  getEditableFieldByColumnId,
  resolveDocumentsColumnMenu,
} from "./columnRegistry";
import { isEditableField } from "~/helpers/libraryEditing";
import type { ActiveFieldEdit } from "~/helpers/libraryTypes";
import type { DocumentsListModel } from "~/hooks/useDocumentsListModel";

interface DocumentsListProps {
  model: DocumentsListModel;
}

export const DocumentsList = ({ model }: DocumentsListProps) => {
  const { t } = useTranslation();
  const isReferenceNumberEnabled =
    useFeatureFlagEnabled(featureFlags.referenceNumber) ?? false;
  const isEditingEnabled = useFeatureFlagEnabled(featureFlags.bulk) ?? false;
  const { user } = useUserContext();
  const [hiddenColumnIds, setHiddenColumnIds] = usePersistentState<string[]>(
    "library:documentsList:hiddenColumns",
    [],
  );
  const columnVisibility: VisibilityState = Object.fromEntries(
    hiddenColumnIds.map((id) => [id, false]),
  );
  const handleColumnVisibilityChange = (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
  ) => {
    setHiddenColumnIds((prevHidden) => {
      const prevVisibility: VisibilityState = Object.fromEntries(
        prevHidden.map((id) => [id, false]),
      );
      const next =
        typeof updater === "function" ? updater(prevVisibility) : updater;
      return Object.entries(next).flatMap(([id, isVisible]) =>
        isVisible ? [] : [id],
      );
    });
  };
  const [activeFieldEdit, setActiveFieldEdit] =
    useState<ActiveFieldEdit | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const filteredTagIds = model.filters.getInValues("tag");
  const handleFieldEditClick = useCallback((params: ActiveFieldEdit) => {
    setActiveFieldEdit(params);
  }, []);
  const columnSpecs = useMemo(
    () =>
      getDocumentsListColumnSpecs({
        referenceNumber: isReferenceNumberEnabled,
      }),
    [isReferenceNumberEnabled],
  );
  const columns = useMemo(
    () =>
      createDocumentsColumns({
        t,
        currentUserId: user.id,
        filteredTagIds,
        columnSpecs,
      }),
    [columnSpecs, t, user.id, filteredTagIds],
  );
  const editableFieldByColumnId = useMemo(
    () => getEditableFieldByColumnId(columnSpecs),
    [columnSpecs],
  );

  // eslint-disable-next-line react-hooks/incompatible-library -- acknowledged; not using React Compiler
  const table = useReactTable({
    data: model.filteredDocuments,
    columns,
    state: {
      sorting: model.sorting,
      columnVisibility,
    },
    onSortingChange: model.handleSortingChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id.toString(),
  });
  const getCellProps = useCallback(
    (cell: Cell<DocumentSummary, unknown>, rowData: DocumentSummary) => {
      const editableField = editableFieldByColumnId[cell.column.id];
      const canEditField =
        isEditingEnabled && isEditableField(rowData, editableField);

      if (!canEditField || !editableField) {
        return undefined;
      }

      return {
        className:
          "cursor-pointer rounded-md transition-shadow hover:shadow-[inset_0_0_0_1px_var(--primary)]",
        onClick: () => {
          const versionId = rowData.latestVersion.id;
          handleFieldEditClick({
            documentId: rowData.id,
            versionId: versionId,
            field: editableField,
          });
        },
      };
    },
    [editableFieldByColumnId, handleFieldEditClick, isEditingEnabled],
  );

  if (model.isLoading) {
    return <CenterSpinner withText />;
  }

  if (!model.hasData) {
    return <NoResult message={t(($) => $.library.noDocumentsFound)} />;
  }

  const { columnMenuLabels, resolvedGroups, ungroupedColumns } =
    resolveDocumentsColumnMenu({
      columnSpecs,
      table,
      t,
    });

  return (
    <div className="space-y-4">
      <FieldEditDialog
        activeEdit={activeFieldEdit}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setActiveFieldEdit(null);
          }
        }}
        onSaved={async () => {
          await model.mutate();
        }}
      />
      <Toolbar
        left={
          <>
            <FilterToggleButton
              isOpen={isFiltersOpen}
              onToggle={() => setIsFiltersOpen((current) => !current)}
            />
            <SearchBar
              placeholder={t(($) => $.library.filterByTitlePlaceholder)}
              value={model.titleSearch}
              onSearch={model.handleTitleSearchChange}
              onDebouncedSearch={model.handleTitleSearchChange}
              onClearSearch={() => model.handleTitleSearchChange("")}
            />
          </>
        }
        right={
          <DocumentsColumnsMenu
            ungroupedColumns={ungroupedColumns}
            resolvedGroups={resolvedGroups}
            columnMenuLabels={columnMenuLabels}
          />
        }
      />

      <FilterableContentLayout
        contentClassName="space-y-4"
        filters={<DocumentFiltersPanel filters={model.filters} />}
        isFiltersOpen={isFiltersOpen}
        sidebarLabel={t(($) => $.commons.filter.title)}
      >
        <DocumentActiveFilterChips filters={model.filters} />
        <DataTable
          table={table}
          noResultsLabel={t(($) => $.library.noResults)}
          getCellProps={getCellProps}
          wrapperRef={tableWrapperRef}
          wrapperClassName={hiddenNativeScrollbarClass}
          virtualizeRows
        />
      </FilterableContentLayout>
      <StickyHorizontalScrollbar
        targetRef={tableWrapperRef}
        deps={[
          model.filteredDocuments,
          columnVisibility,
          isFiltersOpen,
          columns,
        ]}
        className="fixed bottom-0 z-50 h-4 overflow-y-hidden"
      />
    </div>
  );
};
